// components/AnnouncementPopup.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal, Button, Badge, Spinner
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../services/api';

const AnnouncementPopup = ({ pageSlug = 'home' }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const [imageLoading, setImageLoading] = useState({});

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchAnnouncements();
    loadDismissedAnnouncements();
  }, [pageSlug]);

  useEffect(() => {
    if (announcements.length > 0 && !loading) {
      const firstNonDismissedIndex = announcements.findIndex(ann => 
        !dismissedAnnouncements.includes(ann.id)
      );
      
      if (firstNonDismissedIndex >= 0) {
        setCurrentIndex(firstNonDismissedIndex);
        const shouldShow = checkShouldShowPopup();
        
        if (shouldShow) {
          setTimeout(() => {
            setShowPopup(true);
          }, 1000);
        }
      }
    }
  }, [announcements, loading, dismissedAnnouncements]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/announcements.php`);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        const popupAnnouncements = response.data.data.filter(ann => {
          const showPopupFlag = ann.show_popup === true || 
                               ann.show_popup === 1 || 
                               ann.show_popup === "1";
          
          const isActive = ann.is_active === true || 
                          ann.is_active === 1 || 
                          ann.is_active === "1";
          
          let isWithinDate = true;
          if (ann.start_date || ann.end_date) {
            const now = new Date();
            if (ann.start_date) {
              const startDate = new Date(ann.start_date);
              if (now < startDate) isWithinDate = false;
            }
            if (ann.end_date) {
              const endDate = new Date(ann.end_date);
              if (now > endDate) isWithinDate = false;
            }
          }
          
          let forCurrentPage = true;
          const pageField = ann.pages || ann.page_slug;
          
          if (pageField) {
            let pageSlugs = [];
            
            if (typeof pageField === 'string') {
              try {
                const parsed = JSON.parse(pageField);
                if (Array.isArray(parsed)) {
                  pageSlugs = parsed.map(slug => slug.trim().toLowerCase());
                } else {
                  pageSlugs = pageField.split(',')
                    .map(slug => slug.trim().toLowerCase())
                    .filter(slug => slug.length > 0);
                }
              } catch (e) {
                pageSlugs = pageField.split(',')
                  .map(slug => slug.trim().toLowerCase())
                  .filter(slug => slug.length > 0);
              }
            } else if (Array.isArray(pageField)) {
              pageSlugs = pageField.map(slug => slug.trim().toLowerCase());
            }
            
            const currentPageSlug = pageSlug.toLowerCase();
            
            if (pageSlugs.length === 0 || 
                pageSlugs.includes('all') || 
                pageSlugs.includes('*') ||
                pageSlugs.includes('')) {
              forCurrentPage = true;
            } 
            else if (pageSlugs.includes(currentPageSlug)) {
              forCurrentPage = true;
            }
            else if (currentPageSlug === 'home' && pageSlugs.includes('/')) {
              forCurrentPage = true;
            }
            else if (currentPageSlug === '/' && pageSlugs.includes('home')) {
              forCurrentPage = true;
            }
            else {
              forCurrentPage = false;
            }
          }
          
          return showPopupFlag && isActive && isWithinDate && forCurrentPage;
        });
        
        const sortedAnnouncements = popupAnnouncements.sort((a, b) => {
          const priorityA = parseInt(a.priority) || 0;
          const priorityB = parseInt(b.priority) || 0;
          return priorityB - priorityA;
        });
        
        setAnnouncements(sortedAnnouncements);
      }
    } catch (err) {
      console.error('Error fetching popup announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDismissedAnnouncements = () => {
    const sessionDismissed = sessionStorage.getItem('dismissedAnnouncements');
    const sessionList = sessionDismissed ? JSON.parse(sessionDismissed) : [];
    setDismissedAnnouncements(sessionList);
  };

  const checkShouldShowPopup = () => {
    if (announcements.length === 0) return false;
    
    const currentAnnouncement = announcements[currentIndex];
    if (!currentAnnouncement) return false;
    
    if (dismissedAnnouncements.includes(currentAnnouncement.id)) {
      return false;
    }
    
    return true;
  };

  const handleClose = () => {
    const currentAnnouncement = announcements[currentIndex];
    
    if (currentAnnouncement) {
      const sessionDismissed = JSON.parse(sessionStorage.getItem('dismissedAnnouncements') || '[]');
      if (!sessionDismissed.includes(currentAnnouncement.id)) {
        sessionDismissed.push(currentAnnouncement.id);
        sessionStorage.setItem('dismissedAnnouncements', JSON.stringify(sessionDismissed));
      }
      
      setDismissedAnnouncements(prev => [...prev, currentAnnouncement.id]);
      
      const nextIndex = announcements.findIndex((ann, idx) => 
        idx > currentIndex && !dismissedAnnouncements.includes(ann.id)
      );
      
      if (nextIndex >= 0) {
        setCurrentIndex(nextIndex);
        setTimeout(() => {
          setShowPopup(true);
        }, 300);
      } else {
        setShowPopup(false);
      }
    }
  };

  const getModalSize = (size) => {
    switch (size) {
      case 'small': return 'sm';
      case 'large': return 'lg';
      case 'fullscreen': return 'xl';
      default: return 'md';
    }
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'danger': return 'text-danger';
      case 'warning': return 'text-warning';
      case 'info': return 'text-info';
      case 'success': return 'text-success';
      case 'primary': return 'text-primary';
      case 'secondary': return 'text-secondary';
      case 'dark': return 'text-dark';
      default: return 'text-danger';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getAttachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return null;
    return `${UPLOAD_BASE_URL}${attachmentPath}`;
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return 'fas fa-file';
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fas fa-file-word text-primary';
      case 'txt': return 'fas fa-file-alt text-secondary';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg': return 'fas fa-file-image text-success';
      default: return 'fas fa-file';
    }
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.split('.').pop().toLowerCase();
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);
  };

  // Check if content already contains the attachment image
  const contentHasAttachmentImage = (content, attachment) => {
    if (!content || !attachment) return false;
    const fileName = attachment.split('/').pop();
    // Check if content contains the filename or an img tag with the attachment URL
    return content.includes(fileName) || 
           content.includes(getAttachmentUrl(attachment)) ||
           (content.includes('<img') && content.includes(fileName));
  };

  // Enhanced function to process HTML content and embed images
  const processContentWithImages = (htmlContent, attachment) => {
    if (!htmlContent) return htmlContent;
    
    let processedContent = htmlContent;
    
    // 1. Convert relative image paths to absolute URLs
    processedContent = processedContent.replace(
      /<img[^>]+src="([^"]+)"[^>]*>/gi,
      (match, src) => {
        // If src is a relative path (not starting with http://, https://, or data:)
        if (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
          // Convert to absolute URL
          const absoluteUrl = `${UPLOAD_BASE_URL}${src.startsWith('/') ? src : '/' + src}`;
          return match.replace(src, absoluteUrl);
        }
        return match;
      }
    );
    
    // 2. Handle image links - convert to embedded images
    processedContent = processedContent.replace(
      /<a[^>]+href="([^"]+\.(jpg|jpeg|png|gif|bmp|webp|svg))"[^>]*>([^<]*)<\/a>/gi,
      (match, href, ext, linkText) => {
        const imageUrl = href.startsWith('http') ? href : `${UPLOAD_BASE_URL}${href.startsWith('/') ? href : '/' + href}`;
        return `
          <div class="embedded-image-container">
            <img src="${imageUrl}" alt="${linkText || 'Announcement Image'}" class="embedded-image" />
            ${linkText ? `<p class="image-caption">${linkText}</p>` : ''}
          </div>
        `;
      }
    );
    
    // 3. Wrap content in a container for styling
    return `<div class="announcement-content-wrapper">${processedContent}</div>`;
  };

  // Function to render processed HTML content
  const renderProcessedContent = (htmlContent, attachment) => {
    if (!htmlContent) return null;
    
    const processedHtml = processContentWithImages(htmlContent, attachment);
    
    return (
      <div 
        className="announcement-content-html"
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
    );
  };

  // Function to handle image load events
  const handleImageLoad = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
  };

  const handleImageError = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
  };

  // Function to render image attachment preview ONLY IF not already in content
  const renderImageAttachment = (attachmentPath, content) => {
    if (!attachmentPath || !isImageFile(attachmentPath)) return null;
    
    // Check if image is already embedded in content
    if (contentHasAttachmentImage(content, attachmentPath)) {
      return null; // Don't show attachment preview if already in content
    }
    
    const fileName = attachmentPath.split('/').pop();
    const imageUrl = getAttachmentUrl(attachmentPath);
    const imageId = `attachment-${currentAnnouncement.id}-${fileName}`;
    
    return (
      <div className="image-attachment-preview mt-4 pt-3 border-top">
        <Badge bg="light" text="dark" className="mb-3">
          <i className="fas fa-paperclip me-1"></i>
          Image Attachment
        </Badge>
        
        <div className="image-preview-container text-center position-relative">
          {imageLoading[imageId] !== false && (
            <div className="image-loading-overlay">
              <Spinner animation="border" size="sm" variant="primary" />
              <span className="ms-2 small">Loading image...</span>
            </div>
          )}
          
          <img 
            id={imageId}
            src={imageUrl}
            alt="Attachment"
            className="img-fluid rounded shadow-sm preview-image"
            style={{ 
              maxHeight: '300px',
              cursor: 'zoom-in',
              border: '1px solid #dee2e6',
              display: imageLoading[imageId] === false ? 'block' : 'none'
            }}
            onClick={() => window.open(imageUrl, '_blank')}
            onLoad={() => handleImageLoad(imageId)}
            onError={() => handleImageError(imageId)}
          />
          
          <div className="image-actions mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              href={imageUrl}
              target="_blank"
              className="me-2"
            >
              <i className="fas fa-external-link-alt me-1"></i>
              Open Full Size
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              href={imageUrl}
              download={fileName}
            >
              <i className="fas fa-download me-1"></i>
              Download
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Function to render non-image attachments
  const renderNonImageAttachment = (attachmentPath) => {
    if (!attachmentPath || isImageFile(attachmentPath)) return null;
    
    const fileName = attachmentPath.split('/').pop();
    
    return (
      <div className="non-image-attachment mt-4 pt-3 border-top">
        <Badge bg="light" text="dark" className="mb-3">
          <i className="fas fa-paperclip me-1"></i>
          Attachment
        </Badge>
        
        <a 
          href={getAttachmentUrl(attachmentPath)}
          target="_blank"
          rel="noopener noreferrer"
          className="d-flex align-items-center gap-2 text-decoration-none p-3 bg-light rounded shadow-sm"
        >
          <div className="attachment-icon">
            <i className={`${getFileIcon(attachmentPath)} fa-2x`}></i>
          </div>
          <div className="flex-grow-1">
            <div className="fw-medium">{fileName}</div>
            <small className="text-muted">
              Click to download or view
            </small>
          </div>
          <div className="attachment-action">
            <i className="fas fa-download text-primary fa-lg"></i>
          </div>
        </a>
      </div>
    );
  };

  if (loading || announcements.length === 0 || !announcements[currentIndex]) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  const hasImageAttachment = currentAnnouncement.attachment && isImageFile(currentAnnouncement.attachment);
  const imageAlreadyInContent = contentHasAttachmentImage(currentAnnouncement.content, currentAnnouncement.attachment);

  return (
    <>
      {/* Main Popup Modal */}
      <Modal
        show={showPopup}
        onHide={handleClose}
        size={getModalSize(currentAnnouncement.popup_size)}
        centered
        backdrop={currentAnnouncement.dismissible ? true : 'static'}
        keyboard={currentAnnouncement.dismissible}
        style={{ zIndex: 99999 }}
        className="announcement-popup-modal"
      >
        <Modal.Header 
          className={`border-bottom ${currentAnnouncement.icon_color ? `border-${currentAnnouncement.icon_color}` : 'border-primary'}`}
        >
          <Modal.Title className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className={`rounded-circle bg-${getColorClass(currentAnnouncement.icon_color).replace('text-', '')} bg-opacity-10 p-2 me-3`}>
                  <i className={`${currentAnnouncement.icon || 'fas fa-bullhorn'} 
                    ${getColorClass(currentAnnouncement.icon_color)} fs-4`}></i>
                </div>
                <div>
                  <h5 className="mb-0">{currentAnnouncement.title}</h5>
                  <small className="text-muted">
                    <i className="fas fa-calendar-alt me-1"></i>
                    {formatDate(currentAnnouncement.date)}
                    {currentAnnouncement.start_date && (
                      <>
                        <i className="fas fa-play ms-3 me-1"></i>
                        Starts: {formatDate(currentAnnouncement.start_date)}
                      </>
                    )}
                  </small>
                </div>
              </div>
              
              {/* View All Announcements Link - ROUND ICON */}
              <Link 
                to="/PublicAnnouncements" 
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  textDecoration: 'none',
                  transition: 'all 0.3s'
                }}
                title="View All Announcements"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-bullhorn"></i>
              </Link>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="py-4 announcement-modal-body">
          {/* Announcement Content with Embedded Images */}
          <div className="announcement-content">
            {renderProcessedContent(
              currentAnnouncement.content, 
              currentAnnouncement.attachment
            )}
          </div>
          
          {/* Image Attachment Preview (only if not already in content) */}
          {hasImageAttachment && !imageAlreadyInContent && 
            renderImageAttachment(currentAnnouncement.attachment, currentAnnouncement.content)}
          
          {/* Non-Image Attachment */}
          {currentAnnouncement.attachment && !hasImageAttachment && 
            renderNonImageAttachment(currentAnnouncement.attachment)}
          
          {/* Announcement Metadata */}
          {(currentAnnouncement.category || currentAnnouncement.author) && (
            <div className="announcement-metadata mt-4 pt-3 border-top">
              <div className="row">
                {currentAnnouncement.category && (
                  <div className="col-6">
                    <small className="text-muted">
                      <i className="fas fa-tag me-1"></i>
                      Category: {currentAnnouncement.category}
                    </small>
                  </div>
                )}
                {currentAnnouncement.author && (
                  <div className="col-6 text-end">
                    <small className="text-muted">
                      <i className="fas fa-user me-1"></i>
                      By: {currentAnnouncement.author}
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer className="border-top-0 pt-0">
          <div className="d-flex justify-content-between align-items-center w-100">
            {/* Progress indicator */}
            <div className="text-muted small">
              <i className="fas fa-layer-group me-1"></i>
              {currentIndex + 1} of {announcements.length}
            </div>
            
            {/* Action buttons */}
            <div className="d-flex gap-2">
              {/* Download button for attachments */}
              {currentAnnouncement.attachment && (
                <Button
                  variant="outline-primary"
                  href={getAttachmentUrl(currentAnnouncement.attachment)}
                  target="_blank"
                  size="sm"
                  className="d-flex align-items-center"
                >
                  <i className="fas fa-download me-1"></i>
                  Download
                </Button>
              )}
              
              {/* Next/Close button */}
              {(() => {
                const nextNonDismissedIndex = announcements.findIndex((ann, idx) => 
                  idx > currentIndex && !dismissedAnnouncements.includes(ann.id)
                );
                
                if (nextNonDismissedIndex >= 0) {
                  return (
                    <Button
                      variant="primary"
                      onClick={handleClose}
                      className="d-flex align-items-center"
                    >
                      Next
                      <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  );
                } else {
                  return (
                    <Button
                      variant="primary"
                      onClick={handleClose}
                      className="d-flex align-items-center"
                    >
                      Close
                    </Button>
                  );
                }
              })()}
            </div>
          </div>
          
          {/* Link to view all announcements */}
          <div className="text-center mt-3 pt-2 border-top">
            <Link 
              to="/PublicAnnouncements" 
              className="text-decoration-none text-primary fw-medium"
              style={{ fontSize: '0.9rem' }}
            >
              <i className="fas fa-list me-1"></i>
              View All Announcements
            </Link>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Auto-close timer */}
      {showPopup && currentAnnouncement.popup_duration > 0 && (
        <AutoCloseTimer 
          duration={currentAnnouncement.popup_duration}
          onClose={handleClose}
        />
      )}

      {/* CSS Styles for the component */}
      <style jsx="true">{`
        /* Modal Styling */
        .announcement-popup-modal .modal-content {
          border-radius: 15px;
          overflow: hidden;
          border: none;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          max-width: 95vw;
        }
        
        .announcement-popup-modal .modal-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 3px solid;
          padding: 1.2rem 1.5rem;
        }
        
        .announcement-popup-modal .modal-body {
          font-size: 1.05rem;
          line-height: 1.6;
          max-height: 70vh;
          overflow-y: auto;
          padding: 1.5rem;
        }
        
        /* Announcement Content Styling */
        .announcement-content-wrapper {
          width: 100%;
        }
        
        .announcement-content-wrapper p {
          margin-bottom: 1.2rem;
          color: #333;
        }
        
        .announcement-content-wrapper ul, 
        .announcement-content-wrapper ol {
          padding-left: 2rem;
          margin-bottom: 1.2rem;
        }
        
        .announcement-content-wrapper li {
          margin-bottom: 0.5rem;
        }
        
        .announcement-content-wrapper h1,
        .announcement-content-wrapper h2,
        .announcement-content-wrapper h3,
        .announcement-content-wrapper h4,
        .announcement-content-wrapper h5,
        .announcement-content-wrapper h6 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #2c3e50;
        }
        
        /* Embedded Images Styling */
        .announcement-content-wrapper img,
        .embedded-image,
        .attachment-embedded-image,
        .preview-image {
          display: block !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: 400px !important;
          margin: 1.5rem auto !important;
          border-radius: 10px !important;
          object-fit: contain !important;
          border: 1px solid #dee2e6 !important;
          padding: 5px !important;
          background: white !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
          transition: all 0.3s ease !important;
        }
        
        .embedded-image-container,
        .attachment-image-container {
          margin: 1.5rem 0;
          text-align: center;
        }
        
        .image-caption {
          font-size: 0.9rem;
          color: #6c757d;
          margin-top: 0.5rem;
          font-style: italic;
        }
        
        /* Preview Image Styling */
        .preview-image {
          cursor: zoom-in !important;
          transition: transform 0.3s ease, box-shadow 0.3s ease !important;
        }
        
        .preview-image:hover {
          transform: scale(1.02) !important;
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        
        /* Image Loading Overlay */
        .image-preview-container {
          position: relative;
          min-height: 100px;
        }
        
        .image-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 8px;
          z-index: 10;
        }
        
        /* Image Actions */
        .image-actions {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
        }
        
        /* Attachment Styling */
        .image-attachment-preview,
        .non-image-attachment {
          border-top: 1px solid #dee2e6;
        }
        
        .image-attachment-preview .badge,
        .non-image-attachment .badge {
          margin-bottom: 1rem;
        }
        
        .non-image-attachment a {
          transition: all 0.3s ease;
        }
        
        .non-image-attachment a:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important;
          background: #f8f9fa !important;
        }
        
        .attachment-icon {
          width: 50px;
          text-align: center;
        }
        
        .attachment-action {
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }
        
        .non-image-attachment a:hover .attachment-action {
          opacity: 1;
        }
        
        /* Announcement Metadata */
        .announcement-metadata {
          font-size: 0.9rem;
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .announcement-popup-modal .modal-body {
            max-height: 60vh;
            padding: 1rem;
          }
          
          .announcement-content-wrapper img,
          .embedded-image,
          .attachment-embedded-image,
          .preview-image {
            max-height: 250px !important;
            margin: 1rem auto !important;
          }
          
          .image-actions {
            flex-direction: column;
            align-items: center;
          }
          
          .image-actions .btn {
            width: 100%;
            justify-content: center;
          }
        }
        
        @media (max-width: 576px) {
          .announcement-popup-modal .modal-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .announcement-popup-modal .modal-header .d-flex {
            width: 100%;
            justify-content: space-between;
          }
          
          .announcement-content-wrapper img,
          .embedded-image,
          .attachment-embedded-image,
          .preview-image {
            max-height: 200px !important;
          }
        }
        
        /* Animation for modal */
        @keyframes slideInDown {
          from {
            transform: translateY(-100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .announcement-popup-modal .modal-dialog {
          animation: slideInDown 0.4s ease-out;
        }
        
        /* Custom scrollbar for modal body */
        .announcement-modal-body::-webkit-scrollbar {
          width: 8px;
        }
        
        .announcement-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .announcement-modal-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .announcement-modal-body::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </>
  );
};

// Auto-close timer component
const AutoCloseTimer = ({ duration, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    if (timeLeft <= 0) {
      onClose();
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, onClose]);

  return (
    <div className="position-fixed bottom-0 end-0 m-3" style={{ zIndex: 100000 }}>
      <div className="alert alert-info shadow-sm mb-0 border-0 p-2">
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-clock"></i>
          <span className="small">Closing in {timeLeft}s</span>
        </div>
        <div className="progress mt-1" style={{ height: '3px' }}>
          <div 
            className="progress-bar bg-info" 
            style={{ 
              width: `${(timeLeft / duration) * 100}%`,
              transition: 'width 1s linear'
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;